import { useUserStore } from '@/stores/userStore'
import { useNotificationStore } from '@/stores/notification'
import { useStorage } from 'vue3-storage'

/**
 * Composable for handling route-based authorization
 */
export function useRouteGuard() {
  const userStore = useUserStore()
  const notificationStore = useNotificationStore()

  /**
   * Ensures user permissions are loaded before performing authorization checks
   */
  const ensureUserPermissions = async (): Promise<boolean> => {
    try {
      // Check if user has a valid token first
      const storage = useStorage()
      const token = storage.getStorageSync('token')

      if (!token) {
        console.error('No authentication token found')
        return false
      }

      // If user info is not loaded, try to load it
      if (!userStore.userInfo.id) {
        try {
          await userStore.loadUserInfo()
        } catch (loadError) {
          console.error('Failed to load user info:', loadError)
          return false
        }
      }

      // Return true if user info is available OR if we have a token (user might be authenticated)
      // The actual permission check will happen in requireManagementAccess
      return userStore.userInfo.id !== null || !!token
    } catch (error) {
      console.error('Error loading user permissions:', error)
      return false
    }
  }

  /**
   * Checks if user has management access
   * @param showNotification - whether to show error notification (default: false, since we redirect to no-access page)
   * @returns true if user has access, false otherwise
   */
  const requireManagementAccess = (showNotification: boolean = false): boolean => {
    if (!userStore.canAccessResourcePlanning) {
      if (showNotification) {
        notificationStore.setErrorNotification(
          'Sie besitzen nicht die notwendige Berechtigung für die Ressourcenplanung. Nur die Geschäftsführung hat Zugriff.'
        )
      }
      return false
    }
    return true
  }

  /**
   * Generic permission check with custom error message
   */
  const requirePermission = (
    permission: keyof import('@/stores/userStore').UserPermissions,
    errorMessage?: string
  ): boolean => {
    if (!userStore.hasPermission(permission)) {
      notificationStore.setErrorNotification(
        errorMessage || 'Sie haben keine Berechtigung für diesen Bereich.'
      )
      return false
    }
    return true
  }

  return {
    ensureUserPermissions,
    requireManagementAccess,
    requirePermission
  }
}